import { useCallback, useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

let nativePopGuard: ((event: PopStateEvent) => void) | null = null;
let nativeGuardInstalled = false;

/** Install before BrowserRouter so a rejected pop cannot unmount the editor first. */
export function installUnsavedChangesGuard() {
  if (nativeGuardInstalled) return;
  window.addEventListener('popstate', (event) => nativePopGuard?.(event), true);
  nativeGuardInstalled = true;
}

/** BrowserRouter does not support useBlocker; guard its navigator and native back/forward. */
export function useUnsavedChanges(dirty: boolean) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const confirmDiscard = useCallback(
    () => !dirty || window.confirm('当前修改尚未保存，确定放弃修改？'),
    [dirty],
  );

  useEffect(() => {
    if (!dirty) return;
    let leaving = false;
    let restoring = false;
    const currentIndex: number | undefined = window.history.state?.idx;
    const push = navigator.push;
    const replace = navigator.replace;
    const go = navigator.go;
    navigator.push = (...args) => {
      if (confirmDiscard()) {
        push.apply(navigator, args);
      }
    };
    navigator.replace = (...args) => {
      if (confirmDiscard()) {
        replace.apply(navigator, args);
      }
    };
    navigator.go = (...args) => {
      if (confirmDiscard()) {
        leaving = true;
        go.apply(navigator, args);
      }
    };
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    const popState = (event: PopStateEvent) => {
      if (leaving) {
        leaving = false;
        return;
      }
      if (restoring) {
        restoring = false;
        event.stopImmediatePropagation();
        return;
      }
      const nextIndex = event.state?.idx;
      if (currentIndex == null || nextIndex == null || confirmDiscard()) return;
      event.stopImmediatePropagation();
      restoring = true;
      window.history.go(currentIndex - nextIndex);
    };
    window.addEventListener('beforeunload', beforeUnload);
    nativePopGuard = popState;
    return () => {
      navigator.push = push;
      navigator.replace = replace;
      navigator.go = go;
      window.removeEventListener('beforeunload', beforeUnload);
      if (nativePopGuard === popState) nativePopGuard = null;
    };
  }, [confirmDiscard, dirty, navigator]);

  return confirmDiscard;
}
